<template>
  <vx-card no-shadow>
    <div class="vx-row mb-6 mt-2">
      <div class="vx-col sm:w-4/5 w-full">
        <h2 style="font-size: revert; font-weight: 500">{{ meeting.title }}</h2>
        <h5>Google Docs</h5>
        <p class="opacity-70 mt-2">
          Choose a Google Doc or Sheet from <b>Venue Drive / Meeting Templates</b>. When a meeting is started, the
          template is copied into <b>Venue Drive / &lt;venue&gt; / Meetings / {{ meeting.title }}</b> and shown in the
          meeting sidebar for Power BI steps without questions, and across the full width in Google Doc/Sheet steps.
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-6">
      <div class="flex">
        <div class="w-64 flex-shrink-0">
          <span>Template</span>
        </div>
        <div class="flex-grow">
          <vs-select v-model="templateFileId" class="w-full" :disabled="isLoadingTemplates" autocomplete>
            <vs-select-item :value="null" text="None" />
            <vs-select-item
              v-for="template in templates"
              :key="template.id"
              :value="template.id"
              :text="`${template.name} (${mimeTypeLabel(template.mimeType)})`"
            />
          </vs-select>
          <p v-if="templatesError" class="text-danger text-sm mt-1">{{ templatesError }}</p>
          <p v-else-if="isMissingTemplate" class="text-danger text-sm mt-1">
            The selected template is no longer in Meeting Templates. Choose another one.
          </p>
          <a
            v-else-if="selectedTemplate"
            :href="selectedTemplate.webViewLink"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm mt-1 inline-block"
          >
            Open template in Google
          </a>
        </div>
      </div>

      <div class="flex">
        <div class="w-64 flex-shrink-0">
          <span>Copy</span>
        </div>
        <div class="flex-grow">
          <vs-select v-model="documentCopyMode" class="w-full" :disabled="!templateFileId">
            <vs-select-item
              v-for="mode in copyModes"
              :key="mode.value"
              :value="mode.value"
              :text="mode.text"
            />
          </vs-select>
          <p class="opacity-70 text-sm mt-1">{{ copyModeDescription }}</p>
        </div>
      </div>
    </div>

    <vs-row vs-justify="space-between" class="flex flex-wrap items-center mt-6" v-if="hasPermission('vp.life.meetings.edit')">
      <vs-button @click="onSave">Save</vs-button>
    </vs-row>
  </vx-card>
</template>

<script>
import * as getMeetingTemplates from "@/api/vp/meetings/getMeetingTemplates";

const MIME_DOC = "application/vnd.google-apps.document";
const MIME_SHEET = "application/vnd.google-apps.spreadsheet";

export default {
  props: {
    meeting: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      templates: [],
      isLoadingTemplates: true,
      templatesError: null,
      copyModes: [
        { value: "NewCopyEveryMeeting", text: "New copy every meeting" },
        { value: "OneCopy", text: "One copy" },
        { value: "NewCopyPerYear", text: "New copy per year" }
      ]
    };
  },
  computed: {
    templateFileId: {
      get() {
        return this.meeting.templateFileId || null;
      },
      set(value) {
        this.$set(this.meeting, "templateFileId", value || null);
        if (value && !this.meeting.documentCopyMode) {
          this.$set(this.meeting, "documentCopyMode", "NewCopyEveryMeeting");
        }
      }
    },
    documentCopyMode: {
      get() {
        return this.meeting.documentCopyMode || "NewCopyEveryMeeting";
      },
      set(value) {
        this.$set(this.meeting, "documentCopyMode", value);
      }
    },
    selectedTemplate() {
      return this.templates.find(t => t.id === this.templateFileId) || null;
    },
    isMissingTemplate() {
      return !this.isLoadingTemplates && !!this.templateFileId && !this.selectedTemplate;
    },
    copyModeDescription() {
      const title = this.meeting.title;
      switch (this.documentCopyMode) {
        case "OneCopy":
          return `The template is copied once per venue as "${title}". Every meeting opens that same file, regardless of year.`;
        case "NewCopyPerYear":
          return `The template is copied once per venue per year as "${title} - <year>". Every meeting that year opens that file.`;
        default:
          return `Every meeting gets its own copy, named "${title} - <period>", e.g. "${title} - Week 37, 2026".`;
      }
    }
  },
  methods: {
    mimeTypeLabel(mimeType) {
      if (mimeType === MIME_DOC) return "Doc";
      if (mimeType === MIME_SHEET) return "Sheet";
      return mimeType;
    },
    onSave() {
      this.$emit("update:meeting");
    }
  },
  async created() {
    try {
      this.templates = await getMeetingTemplates.get();
    } catch (err) {
      console.error(err);
      this.templatesError = "Could not load templates from Venue Drive / Meeting Templates.";
    } finally {
      this.isLoadingTemplates = false;
    }
  }
};
</script>
